import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const CurrencyConversionCalculator = () => {
  const [amount, setAmount] = useState('');
  const [sourceCurrency, setSourceCurrency] = useState('USD');
  const [targetCurrency, setTargetCurrency] = useState('EUR');
  const [convertedAmount, setConvertedAmount] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);
  const [exchangeRates, setExchangeRates] = useState({}); // Initialize with an empty object
  const [isLoading, setIsLoading] = useState(true);

  const handleAmountChange = (e) => {
    setAmount(e.target.value);
    setError('');
  };

  const handleSourceCurrencyChange = (e) => {
    setSourceCurrency(e.target.value);
  };

  const handleTargetCurrencyChange = (e) => {
    setTargetCurrency(e.target.value);
  };

  const fetchExchangeRates = async () => {
    try {
      const response = await fetch('https://api.exchangerate.host/latest?base=USD');
      const data = await response.json();
      if (data.success) {
        setExchangeRates(data.rates);
      } else {
        setError(data.error.info);
      }
      setIsLoading(false);
    } catch (error) {
      console.error('Failed to fetch exchange rates:', error);
      setError('Failed to fetch exchange rates. Please try again later.');
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchExchangeRates();
  }, []);

  const convertCurrency = () => {
    if (!amount || !exchangeRates[sourceCurrency] || !exchangeRates[targetCurrency]) {
      setError('Please enter a valid amount and select valid currencies.');
      return;
    }

    const amountValue = parseFloat(amount);
    if (isNaN(amountValue)) {
      setError('Please enter a valid amount.');
      return;
    }

    const convertedValue = amountValue * (exchangeRates[targetCurrency] / exchangeRates[sourceCurrency]);
    setConvertedAmount(convertedValue.toFixed(2));

    // Update chart data
    setChartData({
      labels: [sourceCurrency, targetCurrency],
      datasets: [
        {
          label: 'Currency Conversion',
          data: [amountValue, convertedValue],
          backgroundColor: ['rgba(75, 192, 192, 0.5)', 'rgba(54, 162, 235, 0.5)'],
          borderColor: ['rgba(75, 192, 192, 1)', 'rgba(54, 162, 235, 1)'],
          borderWidth: 1
        }
      ]
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    convertCurrency();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="text-center">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-green-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading exchange rates...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Currency Conversion Calculator | BioLab Tools</title>
        <meta name="description" content="Convert amounts between different currencies with ease. Use this calculator to get accurate currency conversions." />
        <meta name="keywords" content="currency conversion calculator, convert currency, exchange rates, currency converter" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4 max-w-7xl">
          <header className="mb-8 text-center">
            <div className="text-4xl font-bold text-green-600 mb-2">🔍</div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">Currency Conversion Calculator</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Convert amounts between different currencies with ease. Get accurate currency conversions.
            </p>
          </header>

          <div className="bg-white p-6 rounded-lg shadow-md max-w-2xl mx-auto">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Amount</label>
                <input
                  type="number"
                  value={amount}
                  onChange={handleAmountChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Enter amount"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Source Currency</label>
                <select
                  value={sourceCurrency}
                  onChange={handleSourceCurrencyChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  disabled={Object.keys(exchangeRates).length === 0}
                >
                  {Object.keys(exchangeRates).length > 0 && Object.keys(exchangeRates).map(currency => (
                    <option key={currency} value={currency}>{currency}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Target Currency</label>
                <select
                  value={targetCurrency}
                  onChange={handleTargetCurrencyChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  disabled={Object.keys(exchangeRates).length === 0}
                >
                  {Object.keys(exchangeRates).length > 0 && Object.keys(exchangeRates).map(currency => (
                    <option key={currency} value={currency}>{currency}</option>
                  ))}
                </select>
              </div>

              <button
                type="submit"
                className="w-full bg-green-600 text-white py-3 px-4 rounded-md font-semibold hover:bg-green-700 transition duration-200"
              >
                Convert
              </button>

              {error && (
                <div className="bg-red-100 text-red-700 p-4 rounded-md mt-4">
                  {error}
                </div>
              )}

              {convertedAmount !== null && (
                <div className="mt-8">
                  <h2 className="text-2xl font-bold text-gray-800 mb-6">Result</h2>
                  <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                    <p className="text-lg font-medium">{amount} {sourceCurrency} is equal to {convertedAmount} {targetCurrency}</p>
                  </div>

                  <div className="mt-8">
                    <h3 className="text-xl font-bold text-gray-800 mb-4">Visualization</h3>
                    <div className="h-96">
                      <Bar
                        data={chartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          scales: {
                            y: {
                              beginAtZero: true,
                              title: { display: true, text: 'Amount' }
                            },
                            x: { title: { display: true, text: 'Currency' } }
                          },
                          plugins: { title: { display: true, text: 'Currency Conversion' } }
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </form>
          </div>

          {/* FAQ Section */}
          <div className="mt-16 bg-white p-6 rounded-lg shadow-md max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Frequently Asked Questions (FAQ)</h2>
            <div className="space-y-6">
              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">What is currency conversion?</h3>
                <p className="text-gray-600">
                  Currency conversion is the process of changing one currency into another. It is essential for international trade, travel, and financial transactions.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">How does this calculator work?</h3>
                <p className="text-gray-600">
                  This calculator fetches real-time exchange rates from a reliable API and uses them to convert the specified amount from one currency to another.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">How accurate are the conversions?</h3>
                <p className="text-gray-600">
                  The conversions are highly accurate, using real-time exchange rates from a reliable API. However, slight variations may occur due to market fluctuations.
                </p>
              </div>
            </div>
          </div>

          {/* How to Use Section */}
          <div className="mt-16 bg-white p-6 rounded-lg shadow-md max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">How to Use the Currency Conversion Calculator</h2>
            <div className="space-y-8">
              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 1: Enter Amount</h3>
                <p className="text-gray-600">
                  Input the amount you want to convert.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 2: Select Source Currency</h3>
                <p className="text-gray-600">
                  Choose the currency of the amount you are converting from.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 3: Select Target Currency</h3>
                <p className="text-gray-600">
                  Choose the currency you want to convert to.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 4: Convert</h3>
                <p className="text-gray-600">
                  Click the "Convert" button to get the converted amount.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 5: View Result and Visualization</h3>
                <p className="text-gray-600">
                  The result will be displayed, along with a bar chart visualizing the conversion.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CurrencyConversionCalculator;